/**
 * Template Detail Page
 * Full page view for managing a single template's configurations
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  useTheme,
  Paper,
  TextField,
} from '@mui/material';
import {
  DataGridPro,
  GridColDef,
  GridRenderCellParams,
  GridToolbar,
} from '@mui/x-data-grid-pro';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useGlobalConfig } from 'context/GlobalContext';
import { BglPrimarySmallButton, BglSecondarySmallButton } from 'components/button/BglButtons';
import { ConfigRegistryApis } from '../api/configRegistryAPI';
import { Template, ConfigRegistryItem } from '../types/configRegistryTypes';
import { getConfigGlobalValue, GlobalConfigValues } from '../utils/templateValueHelpers';
import { EditTemplateValueModal } from '../components/EditTemplateValueModal';
import { ViewConfigModal } from '../components/ViewConfigModal';
import { AddConfigsDialog } from '../components/AddConfigsDialog';
import { CloneTemplateDialog } from '../components/CloneTemplateDialog';
import { createColumnHeader } from '../components/ColumnHeader';
import { SearchableSelect } from 'components/common/SearchableSelect';

export const TemplateDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { rc } = useGlobalConfig();
  const theme = useTheme();

  const [template, setTemplate] = useState<Template | null>(null);
  const [pageConfigs, setPageConfigs] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState<{ configTypes: string[]; dataTypes: string[] }>({ configTypes: [], dataTypes: [] });
  const [memberConfigIds, setMemberConfigIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingIndex, setDeletingIndex] = useState<number | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [addConfigsOpen, setAddConfigsOpen] = useState(false);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<{
    config: ConfigRegistryItem;
    value: string | null;
    index: number;
  } | null>(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  // Server-side search + filters (search input is debounced before fetching)
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [configTypeFilter, setConfigTypeFilter] = useState('');
  const [dataTypeFilter, setDataTypeFilter] = useState('');
  const requestSeqRef = useRef(0);

  const canEdit = rc.permissionsManager.canTemplateEdit();

  // Debounce free-text search
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Fetch one page of template values (server-side pagination + filters)
  const fetchTemplate = useCallback(async () => {
    if (!id) return;

    const seq = ++requestSeqRef.current;
    setIsPageLoading(true);
    setError(null);
    try {
      const { error: err, payload } = await ConfigRegistryApis.getTemplateValuesPage(rc, id, {
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        search: search || undefined,
        configType: configTypeFilter || undefined,
        dataType: dataTypeFilter || undefined,
      });
      if (seq !== requestSeqRef.current) return; // stale response

      if (err || !payload) {
        setError(err?.message || 'Failed to fetch template');
      } else {
        setTemplate({
          id: 0,
          documentId: payload.templateId,
          name: payload.templateName,
          description: payload.description,
          module: payload.module,
          valueCount: payload.memberCount ?? payload.configCount,
          items: [],
        });
        setPageConfigs(payload.configs || []);
        setTotal(payload.configCount || 0);
        setFacets(payload.facets || { configTypes: [], dataTypes: [] });
        setMemberConfigIds(payload.memberConfigIds || []);
      }
    } catch (err: any) {
      if (seq === requestSeqRef.current) setError(err.message || 'An error occurred');
    } finally {
      if (seq === requestSeqRef.current) {
        setIsPageLoading(false);
        setIsLoading(false);
      }
    }
  }, [rc, id, paginationModel, search, configTypeFilter, dataTypeFilter]);

  useEffect(() => {
    fetchTemplate();
  }, [fetchTemplate]);

  // A row's `value` is THIS template's value, so it can't stand in for the
  // default — the GLOBAL values come from their own fetch.
  const [globalValues, setGlobalValues] = useState<GlobalConfigValues>(new Map());
  useEffect(() => {
    let cancelled = false;
    ConfigRegistryApis.getGlobalConfigValues(rc).then(({ values }) => {
      if (!cancelled) setGlobalValues(values);
    });
    return () => { cancelled = true; };
  }, [rc]);

  const handleOpenEditModal = (config: ConfigRegistryItem, value: string | null, index: number) => {
    setSelectedItem({ config, value, index });
    setEditModalOpen(true);
  };

  const handleOpenViewModal = (
    config: ConfigRegistryItem,
    value: string | null,
    index: number
  ) => {
    setSelectedItem({ config, value, index });
    setViewModalOpen(true);
  };

  const handleSaveValue = async (newValue: string) => {
    if (!selectedItem || !template) return;

    await ConfigRegistryApis.upsertTemplateValues(rc, template.documentId, [
      { configDocumentId: selectedItem.config.documentId, value: newValue },
    ]);

    fetchTemplate();
  };

  const handleRemoveConfig = async (index: number) => {
    if (!template) return;

    const configDocumentId = pageConfigs[index]?.documentId;
    if (!configDocumentId) return;

    setDeletingIndex(index);
    try {
      await ConfigRegistryApis.removeTemplateValue(rc, template.documentId, configDocumentId);
      fetchTemplate();
    } catch (err) {
      console.error('Failed to remove config from template:', err);
    } finally {
      setDeletingIndex(null);
    }
  };

  const truncateText = (text: string | null, maxLength: number = 50) => {
    if (!text) return '-';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  // Rows for the current server page (configs arrive flat from /values)
  const rows = useMemo(() => (
    pageConfigs.map((config, index) => ({
      id: `${config.documentId}-${index}`,
      index,
      configKey: config.configKey,
      displayName: config.displayName,
      configType: config.configType,
      configDataType: config.configDataType,
      globalValue: config.value ?? '',
      templateValue: config.value ?? null,
      config,
    }))
  ), [pageConfigs]);

  // Filter options are template-wide facets from the server, not page-derived
  const configTypeOptions = facets.configTypes;
  const dataTypeOptions = facets.dataTypes;

  const filteredRows = rows;

  const hasActiveFilters = Boolean(search.trim() || configTypeFilter || dataTypeFilter);

  useEffect(() => {
    setPaginationModel((m) => ({ ...m, page: 0 }));
  }, [search, configTypeFilter, dataTypeFilter]);

  const columns: GridColDef[] = [
    {
      field: 'serialNo',
      headerName: '#',
      width: 70,
      sortable: false,
      hideable: false,
      resizable: false,
      align: 'center',
      headerAlign: 'center',
      renderHeader: createColumnHeader('#', 'serialNo'),
      renderCell: (params: GridRenderCellParams) => {
        const rowIndex = params.api.getRowIndexRelativeToVisibleRows(params.id);
        const serialNo = paginationModel.page * paginationModel.pageSize + rowIndex + 1;
        return (
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
            {serialNo}
          </Typography>
        );
      },
    },
    {
      field: 'configKey',
      headerName: 'Config Key',
      flex: 1,
      minWidth: 180,
      hideable: false,
      resizable: true,
      renderHeader: createColumnHeader('Config Key', 'configKey'),
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.value || ''} arrow placement="top">
          <Box sx={{
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            color: theme.palette.primary.main,
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {params.value}
          </Box>
        </Tooltip>
      ),
    },
    {
      field: 'displayName',
      headerName: 'Display Name',
      flex: 1.2,
      minWidth: 180,
      resizable: true,
      renderHeader: createColumnHeader('Display Name', 'displayName'),
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.value || ''} arrow placement="top">
          <Box sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {params.value}
          </Box>
        </Tooltip>
      ),
    },
    {
      field: 'configType',
      headerName: 'Config Type',
      flex: 1,
      minWidth: 150,
      resizable: true,
      renderHeader: createColumnHeader('Config Type', 'configType'),
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.value || ''} arrow placement="top">
          <Chip
            label={params.value}
            size="small"
            variant="outlined"
            sx={{
              borderRadius: 1,
              fontWeight: 500,
              fontSize: '0.75rem',
              maxWidth: '100%',
            }}
          />
        </Tooltip>
      ),
    },
    {
      field: 'configDataType',
      headerName: 'Data Type',
      width: 120,
      resizable: true,
      renderHeader: createColumnHeader('Data Type', 'configDataType'),
      renderCell: (params: GridRenderCellParams) => {
        const dataType = params.row.config?.configDataType;
        return (
          <Tooltip title={dataType || ''} placement="top" arrow>
            <Box sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {dataType || '-'}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'templateValue',
      headerName: 'Template Value',
      width: 150,
      resizable: true,
      renderHeader: createColumnHeader('Template Value', 'templateValue'),
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.value || 'Not set'} arrow placement="top">
          <Typography
            variant="body2"
            fontWeight={500}
            noWrap
            sx={{ fontStyle: params.value ? 'normal' : 'italic', color: params.value ? 'text.primary' : 'text.secondary' }}
          >
            {truncateText(params.value) || 'Not set'}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 140,
      sortable: false,
      resizable: false,
      renderHeader: createColumnHeader('Actions', 'actions'),
      cellClassName: 'actions-column',
      headerClassName: 'actions-column-header',
      renderCell: (params: GridRenderCellParams) => {
        const isDeleting = deletingIndex === params.row.index;
        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="View Config">
              <IconButton
                size="small"
                onClick={() => handleOpenViewModal(params.row.config, params.row.templateValue, params.row.index)}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {canEdit && (
              <>
                <Tooltip title="Edit Value">
                  <IconButton
                    size="small"
                    onClick={() => handleOpenEditModal(params.row.config, params.row.templateValue, params.row.index)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remove from Template">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveConfig(params.row.index)}
                    disabled={isDeleting}
                  >
                    {isDeleting ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        );
      },
    },
  ];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !template) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error" action={
          <BglSecondarySmallButton onClick={fetchTemplate}>Retry</BglSecondarySmallButton>
        }>
          {error || 'Template not found'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      p: { xs: 2, sm: 3 },
      height: '100%',
      overflow: 'auto',
      bgcolor: 'background.default'
    }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.6)',
          backdropFilter: 'blur(10px)',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/cms/config-registry')}
            sx={{ cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            Config Registry
          </Link>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/cms/config-registry?tab=templates')}
            sx={{ cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            Templates
          </Link>
          <Typography color="text.primary" variant="body2">{template.name}</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <IconButton onClick={() => navigate('/cms/config-registry?tab=templates')} sx={{ mt: 0.5 }}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h5" fontWeight={600}>
                {template.name}
              </Typography>
              {template.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {template.description}
                </Typography>
              )}
              {template.module && (
                <Chip label={template.module} size="small" sx={{ mt: 1 }} />
              )}
            </Box>
          </Box>
          {canEdit && (
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <BglSecondarySmallButton
                startIcon={<ContentCopyIcon />}
                onClick={() => setCloneOpen(true)}
              >
                Clone Template
              </BglSecondarySmallButton>
              <BglPrimarySmallButton startIcon={<AddIcon />} onClick={() => setAddConfigsOpen(true)}>
                Add Configs
              </BglPrimarySmallButton>
            </Box>
          )}
        </Box>

      {/* Search + filters (local, over template items) */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          label="Search"
          placeholder="Config key, name or value…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ minWidth: 260 }}
        />
        <SearchableSelect
          size="small"
          sx={{ minWidth: 180 }}
          label="Config Type"
          value={configTypeFilter}
          onChange={setConfigTypeFilter}
          options={[{ value: '', label: 'All types' }, ...configTypeOptions.map((t) => ({ value: t, label: t }))]}
        />
        <SearchableSelect
          size="small"
          sx={{ minWidth: 150 }}
          label="Data Type"
          value={dataTypeFilter}
          onChange={setDataTypeFilter}
          options={[{ value: '', label: 'All' }, ...dataTypeOptions.map((t) => ({ value: t, label: t }))]}
        />
        {hasActiveFilters && (
          <BglSecondarySmallButton
            onClick={() => {
              setSearchInput('');
              setSearch('');
              setConfigTypeFilter('');
              setDataTypeFilter('');
            }}
          >
            Clear
          </BglSecondarySmallButton>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          Showing {rows.length} of {total} configs
        </Typography>
      </Box>

      {/* DataGrid */}
      <Box sx={{
        width: '100%',
        height: { xs: 'calc(100vh - 420px)', sm: 'calc(100vh - 380px)' },
        minHeight: 400,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        boxShadow: theme.palette.mode === 'dark'
          ? '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)'
          : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      }}>
        <DataGridPro
          rows={filteredRows}
          columns={columns}
          pagination
          paginationMode="server"
          rowCount={total}
          loading={isPageLoading}
          pageSizeOptions={[25, 50, 100]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          disableRowSelectionOnClick
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: false,
            },
          }}
          sx={{
            border: 'none',
            bgcolor: 'background.paper',
            '& .MuiDataGrid-columnHeaders': {
              bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : '#f8f9fa',
              borderBottom: `2px solid ${theme.palette.divider}`,
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 600,
                fontSize: '0.8125rem',
                color: theme.palette.text.secondary,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              },
            },
            '& .MuiDataGrid-row': {
              transition: 'background-color 0.15s ease',
              '&:hover': {
                bgcolor: theme.palette.mode === 'dark'
                  ? 'rgba(25, 118, 210, 0.12)'
                  : 'rgba(25, 118, 210, 0.06)',
              },
            },
            '& .MuiDataGrid-cell': {
              borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.06)'}`,
              py: 1,
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'center',
              '&:focus': {
                outline: 'none',
              },
              '&:focus-within': {
                outline: 'none',
              },
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: `2px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : '#f8f9fa',
            },
            '& .MuiDataGrid-columnSeparator': {
              display: 'none',
            },
            // Actions column - vertical separator line
            '& .actions-column-header': {
              borderLeft: `2px solid ${theme.palette.divider}`,
            },
            '& .actions-column': {
              borderLeft: `2px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
            },
          }}
        />
      </Box>

        {/* Modals */}
        {selectedItem && (
          <>
            <EditTemplateValueModal
              open={editModalOpen}
              onClose={() => setEditModalOpen(false)}
              onSave={handleSaveValue}
              configKey={selectedItem.config.configKey}
              displayName={selectedItem.config.displayName}
              currentValue={selectedItem.value}
              defaultValue={getConfigGlobalValue(selectedItem.config, globalValues) || null}
              configDataType={selectedItem.config.configDataType}
            />
            <ViewConfigModal
              open={viewModalOpen}
              onClose={() => setViewModalOpen(false)}
              config={selectedItem.config}
              templateValue={selectedItem.value}
            />
          </>
        )}

        {template && (
          <AddConfigsDialog
            open={addConfigsOpen}
            onClose={() => setAddConfigsOpen(false)}
            template={template}
            memberConfigIds={memberConfigIds}
            onConfigsAdded={fetchTemplate}
          />
        )}

        {template && (
          <CloneTemplateDialog
            open={cloneOpen}
            onClose={() => setCloneOpen(false)}
            template={template}
            onCloned={(clonedDocumentId) => {
              // Land on the clone — it's what the user just asked to work on.
              // Without an id (older backend response shape) fall back to the list.
              if (clonedDocumentId) {
                navigate(`/cms/config-registry/templates/${clonedDocumentId}`);
              } else {
                navigate('/cms/config-registry?tab=templates');
              }
            }}
          />
        )}
      </Paper>
    </Box>
  );
};

